"use client";
import { BuyerProject } from "@/types/buyerProject";
import { BuyerUnit } from "@/types/buyerUnit";
import { MapItemSUP } from "@/types/search";
import { SellerUnitsAndProjectsOne } from "@/types/sup";

import { SkeletonLoader } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useBuyerActions } from "@/hooks/use-buyer-actions";
import { BuyerRequestContext } from "@/hooks/use-buyer-request-data";
import { useGuardedAction } from "@/hooks/use-guarded-action";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { useComparisonStoreData } from "@/providers/global";
import { useBuyerPropertiesStore } from "@/providers/global/stores/buyer-properties-store";
import { useBuyerHelpers } from "@/utils/buyer/helpers";
import { formatRange } from "@/utils/format-range";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { CircleOffIcon } from "lucide-react";
import { useCallback, useContext } from "react";
import { ShortlistButton } from "../customer-care-widget/buttons";
import { CompareButton } from "../search/compare/compare-button";

interface SUPTitleProps {
	supOne: SellerUnitsAndProjectsOne;
	onLike?: () => void;
	isLiked?: boolean;
	isLoading?: boolean;
	className?: string;
	isNotAvailableForSale: boolean;
	showDetailsEvenIfUnavailable?: boolean;
	isInPreferenceCard?: boolean;
}

function getSubType(propertyTypeObj: SellerUnitsAndProjectsOne["propertyTypeObj"]): string {
	if (!propertyTypeObj) return "";
	switch (propertyTypeObj?.type) {
		case "apartment":
			return propertyTypeObj.villament ? "Villametn" : propertyTypeObj.penthouse ? "Penthouse" : "Apartment";
		case "house":
			return propertyTypeObj.project ? toSentenceCase(propertyTypeObj.villaType || "Villa") : "House";
		default:
			return toSentenceCase(propertyTypeObj?.type || "Property");
	}
}

export function SUPTitle({
	supOne,
	isLoading,
	className = "",
	isNotAvailableForSale,
	showDetailsEvenIfUnavailable,
	isInPreferenceCard = false,
}: SUPTitleProps) {
	const unitId = supOne.type === "unit" ? supOne._id : null;
	const title = getTitle(supOne);
	const hasTitle = !!(supOne.projectName || supOne.propertyTypes?.length || supOne.bedroom || supOne.bedrooms?.[0]);

	// --- State and Context ---
	const { isAuthenticated, user, refetchUser } = useAuth();
	const { openDialog } = useLoginDialog();
	const { isPropertyShortlisted } = useBuyerPropertiesStore();
	const { data, refetch, actions, setActions } = useContext(BuyerRequestContext) || {};

	// Use centralized buyer actions hook
	const {
		handleShortlist: handleShortlistAction,
		handleRemoveShortlist: handleRemoveShortlistAction,
		isShortlistLoading,
	} = useBuyerActions();

	// Use buyer helpers
	const { getBuyerGroupId, createBuyerGroup } = useBuyerHelpers(user, refetchUser);

	// Comparison tray integration
	const comparisonTray = useComparisonStoreData(state => state.comparisonTray);
	const toggleComparison = useComparisonStoreData(state => state.toggleComparison);
	const isInComparison = comparisonTray?.some(f => f._id === supOne._id) || false;

	// --- Derived State ---
	const pdValues = data?.buyerUnits?.find(({ unit: u }) => u._id === supOne._id);
	const isC2cVisitRequested = data?.projectVisitC2c?.find(({ project: p }) => p.id === supOne.projectId);
	const isVisitRequested =
		user && actions?.requestVisitUnits.includes(unitId ?? "")
			? true
			: isC2cVisitRequested || pdValues?.processDetails?.[2]?.status === "requested";
	// Use central store as primary source of truth for shortlist status
	const propertyIdForShortlist = unitId ? supOne._id : (supOne.projectId ?? "");
	const isShortlisted = isPropertyShortlisted(propertyIdForShortlist);

	// --- Shortlist Handler Logic (without guard) ---
	const performShortlistAction = useCallback(async () => {
		if (!supOne._id) return;

		let buyerGroupId = getBuyerGroupId();
		if (!buyerGroupId) {
			buyerGroupId = await createBuyerGroup();
		}
		if (!buyerGroupId) return;

		try {
			// Create property object based on type field
			const isUnitType = supOne.type === "unit";
			const property: BuyerUnit | BuyerProject = isUnitType
				? ({
						__typename: "BuyerUnit",
						id: supOne._id,
						unit: { id: supOne._id },
					} as BuyerUnit)
				: ({
						__typename: "BuyerProject",
						id: supOne.projectId ?? "",
						project: { id: supOne.projectId ?? "" },
					} as BuyerProject);

			if (isShortlisted) {
				// Use centralized remove shortlist
				await handleRemoveShortlistAction({ property, source: "property_detail" });
			} else {
				// Use centralized shortlist
				await handleShortlistAction(property, "property_detail");
			}

			// Refetch for other components that might depend on BuyerRequestContext
			refetch && refetch();

			// Update actions state for backward compatibility
			if (setActions) {
				if (isShortlisted) {
					setActions({
						shortlistUnits: actions?.shortlistUnits.filter(id => id !== supOne._id) || [],
						requestVisitUnits: actions?.requestVisitUnits || [],
						shortlistProjects: actions?.shortlistProjects || [],
						requestVisitProjects: actions?.requestVisitProjects || [],
					});
				} else {
					setActions({
						shortlistUnits: [...(actions?.shortlistUnits || []), supOne._id ?? ""],
						requestVisitUnits: actions?.requestVisitUnits || [],
						shortlistProjects: actions?.shortlistProjects || [],
						requestVisitProjects: actions?.requestVisitProjects || [],
					});
				}
			}
		} catch (_e) {
			// Error handling is done in useBuyerActions
		}
	}, [
		supOne._id,
		supOne.projectId,
		supOne.type,
		isShortlisted,
		createBuyerGroup,
		getBuyerGroupId,
		handleShortlistAction,
		handleRemoveShortlistAction,
		setActions,
		actions,
		refetch,
	]);

	// Use guarded action hook
	const { guardedAction: handleShortlist, GuardComponent } = useGuardedAction(
		"shortlist_property",
		performShortlistAction,
	);

	return (
		<>
			<div className={`flex justify-between items-start gap-3 ${className}`}>
				<div className={`${!isNotAvailableForSale && "flex-1"} max-w-[75%]`}>
					{hasTitle || !isLoading ? (
						<h1 className="text-xl truncate font-medium text-gray-900 mb-1">{title}</h1>
					) : (
						<SkeletonLoader height="28px" className="mb-1" />
					)}
					<div className="flex items-center gap-2">
						{supOne.identifier && (
							<>
								<span className="text-xs">
									{isLoading ? <SkeletonLoader height="16px" width="30%" /> : supOne.identifier}
								</span>
								<span className="text-xs font-semibold">•</span>
							</>
						)}
						<span
							className="text-xs font-semibold text-gray-500 truncate max-w-[200px]"
							title={
								isLoading ? "" : getSubType(supOne.propertyTypeObj) + (supOne.locality ? ` in ${supOne.locality}` : "")
							}
						>
							{isLoading ? (
								<SkeletonLoader height="16px" width="30%" />
							) : (
								getSubType(supOne.propertyTypeObj) + (supOne.locality ? ` in ${supOne.locality}` : "")
							)}
						</span>
					</div>
				</div>

				{!isInPreferenceCard && (
					<div className="flex items-center gap-2">
						{isNotAvailableForSale ? (
							<div className="flex items-center gap-1 text-md text-primary font-large">
								<CircleOffIcon className="w-4 h-4" />
								<span>Not Available</span>
							</div>
						) : (
							<>
								{/* Compare Button */}
								<CompareButton
									property={supOne as MapItemSUP}
									isInComparison={isInComparison}
									onToggleComparison={toggleComparison}
									variant="icon"
								/>

								{/* Shortlist Button */}
								{!isAuthenticated && (
									<ShortlistButton
										handleShortList={() => openDialog({ onLoginSuccess: handleShortlist })}
										isLoading={false}
										isShortlisted={false}
										isDisabled={false}
									/>
								)}
								{isAuthenticated && (
									<span title={isVisitRequested ? "Cannot remove shortlisting when a visit is already requested." : ""}>
										<ShortlistButton
											handleShortList={handleShortlist}
											isLoading={isShortlistLoading}
											isShortlisted={isShortlisted ?? false}
											isDisabled={!!isVisitRequested}
										/>
									</span>
								)}
							</>
						)}
					</div>
				)}
			</div>
			{/* Sticky Banner for Not Available Units */}
			{!showDetailsEvenIfUnavailable && isNotAvailableForSale && (
				<div className="sticky top-0 z-50 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200 py-3 mb-4">
					<div className="max-w-7xl mx-auto px-4">
						<div className="flex items-center justify-center gap-2 text-sm lg:text-base text-center text-red-800 leading-relaxed">
							<CircleOffIcon className="w-5 h-5 flex-shrink-0" />
							<span className="font-medium">
								This unit is not available for sale right now. Please check other properties.
							</span>
						</div>
					</div>
				</div>
			)}

			{/* Relationship Guard Modal */}
			<GuardComponent />
		</>
	);
}

function getTitle(supOne: SellerUnitsAndProjectsOne) {
	const bedroom = formatRange({ data: (supOne.bedrooms || []).filter(Boolean), withSpaces: false });
	const bedroomText = bedroom ? `${bedroom}BHK ` : "";
	const plotAreas = formatRange({ data: (supOne.plotAreas || []).filter(Boolean), withSpaces: false });
	const isPlot = supOne.propertyTypes?.includes("plot");
	const plotText = isPlot && plotAreas ? `${plotAreas} sqft Plot` : "";

	if (supOne.projectName) {
		const projectName = supOne.projectName
			.split("(")[0]
			.replace(/apartments?/i, "")
			.trim();

		return (
			<>
				{bedroomText} {plotText && <>{plotText} </>}in{" "}
				<a
					href={`/projects/${supOne.projectId}`}
					target="_blank"
					rel="noopener noreferrer"
					className="font-semibold text-primary underline"
				>
					{projectName}
				</a>
			</>
		);
	} else if (supOne.propertyTypes && supOne.propertyTypes.length > 0) {
		// If it's a plot and we already have plotText, don't add the property type again
		if (isPlot && plotText) {
			return plotText;
		}
		return bedroomText + toSentenceCase(supOne.propertyTypes[0]);
	}
	return bedroomText;
}
